import { AxiosResponse } from "axios";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useDebounce } from "use-debounce";

import { Input, UpdateStateIndicator } from "@digital-services/ui";
import { isNotEmpty, isRegExMatch, useInput } from "@ds-cps/ui";

import { AdminPortalUserRole } from "@organization/models/OrganizationUserDetails";
import { useInviteFormContext } from "@user/context";
import { UserState } from "@user/models/UserByEmailResponse";
import { useGetUserByEmail } from "@user/services/use-get-user-by-email";
import { useGetUserByOrganizationId } from "@user/services/use-get-user-by-organization-id";

import { StatusAndProblemDetails } from "@utils/axios-utils";
import { EMAIL_REGEXP } from "@utils/validator-helpers";

interface Props {
  label: string;
  value: string | undefined;
  placeholder: string;
  onInputChange: (newValue: string, isValid: boolean) => void;
}

export const EmailAdvancedField: FunctionComponent<Props> = props => {
  const { label, value, placeholder, onInputChange } = props;

  const { organizationId } = useParams();

  const [formState, setFormState] = useState({
    helperInfo: "",
    pseudoError: false
  });

  const { t: tCommon } = useTranslation();

  const { t: tAdmins } = useTranslation(undefined, {
    keyPrefix: "organization.admins"
  });

  const infoWhenUserExists = tAdmins("invite.info-when-exists");
  const infoWhenInviteExpired = tAdmins("invite.info-when-invite-expired");
  const infoWhenUserIsOrganizationUser = tAdmins(
    "invite.info-when-user-is-organization-user"
  );

  const { setInviteInfoObj } = useInviteFormContext();

  const {
    value: email,
    isValid,
    errorMessage,
    valueChangeHandler
  } = useInput(
    [
      // TBD refactor
      isNotEmpty(() =>
        tCommon("validation.string.not-empty", { fieldName: label })
      ),
      isRegExMatch(EMAIL_REGEXP, () =>
        tCommon("validation.string.invalid-email", {
          fieldName: label
        })
      )
    ],
    value
  );

  useEffect(() => {
    // As soon as we type email address and it's valid, we need to disable Submit button, until Server response arrived
    if (email !== undefined) {
      setInviteInfoObj({
        disableSubmit: true
      });
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const [debouncedEmailValue] = useDebounce(isValid ? email : null, 2000);

  const {
    data: dataFromUserByEmail,
    isLoading: isLoadingByEmail,
    error: errorFromUserByEmail
  } = useGetUserByEmail(debouncedEmailValue || null);

  const {
    data: dataFromUserByOrg,
    isLoading: isLoadingByOrg,
    error: errorFromUserByOrg
  } = useGetUserByOrganizationId(
    organizationId!,
    dataFromUserByEmail ? dataFromUserByEmail.userId : null
  );

  useEffect(() => {
    if (errorFromUserByEmail?.status === 400) {
      // Frontend provides UI block when email address is NOT valid, but sometimes when useDebounces is used, it might delay and request can go with not valid email
      // When email is NOT valid
      const errorsObj = (errorFromUserByEmail?.response as AxiosResponse)?.data
        ?.errors;

      if (Object.keys(errorsObj).includes("email")) {
        setFormState({ helperInfo: errorsObj.email[0], pseudoError: true });
      }
    } else if (errorFromUserByEmail?.status === 404) {
      // When NO user with such valid email
      const dataInError = errorFromUserByEmail?.response
        ?.data as StatusAndProblemDetails;

      if (Object.keys(dataInError).includes("detail")) {
        setInviteInfoObj({
          displayName: "",
          disableSubmit: false,
          reInviteMode: false
        });
        setFormState({ helperInfo: "", pseudoError: false });
      }
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFromUserByEmail]);

  useEffect(() => {
    switch (dataFromUserByEmail?.state) {
      case UserState.Inactive:
      case UserState.InvitationExpired:
        // ReInvite use case
        setInviteInfoObj({
          displayName: dataFromUserByEmail?.displayName,
          reInviteMode: true,
          disableSubmit: false
        });

        setFormState({ helperInfo: infoWhenInviteExpired, pseudoError: false });
        break;

      case UserState.Invited:
      case UserState.Active:
        // It covers users with "invitationExpiration": null (state = Active)
        // and also user with FUTURE "invitationExpiration": "2026-05-27T13:03:21.8452171+02:00" (state = Invited or Active)

        if (errorFromUserByOrg) {
          // Use Case - means that GET `/organizations/${organizationId}/users/${userId}` was 404 and no `data` returned
          // But it's OK for us - user exists, but not in this organization
          setInviteInfoObj({
            displayName: dataFromUserByEmail?.displayName,
            reInviteMode: false,
            disableSubmit: false
          });

          setFormState({ helperInfo: "", pseudoError: false });
        } else if (
          dataFromUserByOrg?.organizationRole ===
          AdminPortalUserRole.OrganizationUser
        ) {
          // Use case - re-invite existed user with role "Organization User"
          setInviteInfoObj({
            displayName: dataFromUserByOrg?.displayName,
            reInviteMode: true,
            disableSubmit: false
          });

          setFormState({
            helperInfo: infoWhenUserIsOrganizationUser,
            pseudoError: false
          });
        } else if (
          dataFromUserByOrg?.organizationRole ===
          AdminPortalUserRole.OrganizationAdmin
        ) {
          // Use Case - means re-invite existed user with "Organization Admin" role
          setInviteInfoObj({
            displayName: dataFromUserByEmail?.displayName,
            reInviteMode: false,
            disableSubmit: true
          });

          setFormState({
            helperInfo: infoWhenUserExists,
            pseudoError: true
          });
        }

        break;

      default:
        break;
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFromUserByEmail, dataFromUserByOrg, errorFromUserByOrg]);

  const hasError = () => {
    if (formState.pseudoError) {
      // No matter if GET /users/{email} is OK (200, 201, 404) or /organizations/{organizationId}/users/{userId}
      // We need to use red validation message placeholder beneath the Email field
      return true;
    }

    // Default UI validation logic
    return !isValid;
  };

  const modifyHelperText = () => {
    // Default UI validation logic
    return isValid ? undefined : errorMessage;
  };

  const isLoading = isLoadingByEmail || isLoadingByOrg;

  const endInputAdornment = isLoading ? (
    <UpdateStateIndicator
      isUpdating={isLoadingByEmail}
      isUpdated={!isLoadingByOrg}
      setIsUpdated={() => {}}
    />
  ) : null;

  // Using className="cursor-progress" for <Input> causes VERY ugly UI.
  // TBD. Maybe, it will be fixed in the future => https://dev.azure.com/abbevci/DS.Chapter.Dev.FE/_workitems/edit/291049
  return (
    <Input
      inputBaseClasses={isLoading ? "cursor-progress" : ""} // better UI, but not as I expected
      autoComplete="email"
      data-testid="email-advanced"
      id="emailAdvanced"
      type="email"
      label={label}
      placeholder={placeholder}
      error={hasError()}
      helperText={formState.helperInfo || modifyHelperText()}
      value={email}
      endInputAdornment={endInputAdornment}
      onChange={event => {
        setFormState({
          helperInfo: "",
          pseudoError: false
        });
        const { value, isValid } = valueChangeHandler(
          event.target.value.replace(" ", "")
        );

        if (value) {
          onInputChange(value, isValid);
          setInviteInfoObj({
            disableSubmit: !isValid,
            reInviteMode: false
          });
        }
      }}
    />
  );
};
